import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import CartBottomSheetNudge from 'shared/components/CartComponent/CartBottomSheetNudge';
import CartNudgeContext from './CartNudgeContext';
import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';

// Default auto-close timer in milliseconds (60 seconds)
const DEFAULT_AUTO_CLOSE_TIMER = 60000;


const CartNudgeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [nudgeEnabled, setNudgeEnabled] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const isShowingRef = useRef(false);
  
  // Get cart nudge configuration from remote config
  const cartNudgeConfig = useRemoteConfig('cartNudgeConfig');
  
  // Get auto-close timer from config, fallback to default
  const autoCloseTimer = useMemo(
    () => cartNudgeConfig?.autoCloseTimer || DEFAULT_AUTO_CLOSE_TIMER,
    [cartNudgeConfig]
  );
  
  const showNudge = useCallback(() => {
    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    
    // Only set state if not already showing to prevent multiple instances
    if (!isShowingRef.current) {
      isShowingRef.current = true;
      setNudgeEnabled(true);
    }
    
    // Always reset auto-close timer
    timerRef.current = setTimeout(() => {
      isShowingRef.current = false;
      setNudgeEnabled(false);
    }, autoCloseTimer);
  }, [autoCloseTimer]);

  const hideNudge = useCallback(() => {
    isShowingRef.current = false;
    setNudgeEnabled(false);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({ showNudge, hideNudge }), [showNudge, hideNudge]);

  return (
    <CartNudgeContext.Provider value={contextValue}>
      {children}
      {nudgeEnabled && <CartBottomSheetNudge key="cart-nudge-sheet" hide={hideNudge} />}
    </CartNudgeContext.Provider>
  );
};

export default CartNudgeProvider;
